import { useEffect, useState } from 'react';
import { ReactComponent as ArrowRightIcon } from '@rocco/icons/svg/arrow/arrow-right.svg';
import type { FilterConfigBarItemPrice } from '@rocco/ui/filter-sort/types/filter-config-bar';
import type { RangeFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/range-filter-state';
import type { FilterBarElementProps } from '@rocco/ui/filter-sort/components/filters-bar/FiltersBarElement';
import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import { Label as RoccoLabel } from '@rocco/components/label';
import { Input as RoccoInput } from '@rocco/components/input';
import { Button as RoccoButton } from '@rocco/components/button';
import { useFilterDropdownContext } from '@rocco/ui/filter-sort/components/filters-bar/FilterDropdown';

export const FiltersBarPrice = (
    props: FilterBarElementProps<FilterConfigBarItemPrice, RangeFilterState>,
) => {
    const { filterConfigItem, filterState, onFilterStateUpdate } = props;
    const { closeFilterDropdown } = useFilterDropdownContext();

    const [active, isActive] = useState(
        filterState.value.min !== filterState.range.min ||
            filterState.value.max !== filterState.range.max,
    );

    const [isAllowedToFilter, setIsAllowedToFilter] = useState(true);

    const [localRange, setLocalRange] = useState([
        filterState.value.min,
        filterState.value.max,
    ]);

    useEffect(() => {
        setIsAllowedToFilter((localRange[1] ?? 0) > (localRange[0] ?? 0));
    }, [localRange]);

    const handleChange = () => {
        if (active) {
            onFilterStateUpdate(
                {
                    ...filterState,
                    value: {
                        min: localRange[0] ?? 0,
                        max: localRange[1] ?? 0,
                    },
                },
                true,
            );

            closeFilterDropdown();
        }
    };

    return (
        <div className="flex flex-col flex-wrap gap-2">
            <div
                className="group flex h-8 cursor-pointer items-center rounded border border-transparent px-2 hover:border-black hover:bg-primary-foreground hover:text-primary"
                onClick={() => {
                    isActive(!active);
                }}
            >
                <RoccoCheckbox
                    id={filterConfigItem.filterKey}
                    checked={active}
                    groupHover="light"
                />

                <RoccoLabel target="checkbox">
                    {filterConfigItem.label ?? 'Price Range'}
                </RoccoLabel>
            </div>

            <div
                className={!active ? 'hidden' : 'flex w-54 flex-col gap-2 pt-4'}
            >
                <div className="flex flex-col gap-2">
                    <span className="text-2.5 font-medium tracking-wider text-gray-900">
                        MIN
                    </span>

                    <RoccoInput
                        type="number"
                        prefix="$"
                        color="light"
                        focus="bordered"
                        shape="rounded"
                        fontSize="sm"
                        value={(localRange[0] ?? 0) / filterState.rangeDivisor}
                        onChange={(e) =>
                            setLocalRange([
                                (parseInt(e.currentTarget.value) || 0) *
                                    filterState.rangeDivisor,
                                localRange[1] ?? 0,
                            ])
                        }
                    />
                </div>

                <div className="box-border flex w-full flex-col gap-2">
                    <span className="text-2.5 font-medium tracking-wider text-gray-900">
                        MAX
                    </span>

                    <RoccoInput
                        type="number"
                        prefix="$"
                        color="light"
                        focus="bordered"
                        shape="rounded"
                        fontSize="sm"
                        value={(localRange[1] ?? 0) / filterState.rangeDivisor}
                        onChange={(e) =>
                            setLocalRange([
                                localRange[0] ?? 0,
                                (parseInt(e.currentTarget.value) || 0) *
                                    filterState.rangeDivisor,
                            ])
                        }
                    />
                </div>

                <div className="mt-8 flex justify-center">
                    <RoccoButton
                        color="secondary"
                        layoutMode="fillFlex"
                        inverted
                        disabled={!isAllowedToFilter}
                        onClick={handleChange}
                    >
                        <span className="text-base font-medium">
                            Show Results
                        </span>{' '}
                        <ArrowRightIcon className="mx-2 size-4 fill-gray-50" />
                    </RoccoButton>
                </div>
            </div>
        </div>
    );
};
